import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
// own
import { Attachment } from '../..';
import { DoublyLinkedList, DoublyLinkedListNode } from '../../../../../submodules/web-shared/data-structures/DoublyLinkedList';

interface AttachmentsStateContextValue<T extends Attachment> {
  attachmentsList: DoublyLinkedList<T>;
  activeAttachmentNode: DoublyLinkedListNode<T> | null;
  moveToNext: () => void;
  moveToPrevious: () => void;
  isPrevDisabled: boolean;
  isNextDisabled: boolean;
  onAttachmentRemove: () => Promise<void>;
  clearActiveAttachment: () => void;
  setCurrentAttachment: (node: DoublyLinkedListNode<T> | null | undefined) => void;
}

const AttachmentsStateContext = createContext<AttachmentsStateContextValue<any> | null>(null);

export const useAttachmentsStateContext = <T extends Attachment>() => useContext(AttachmentsStateContext) as AttachmentsStateContextValue<T>;

interface ProviderProps<T extends Attachment> {
  children: React.ReactNode;
  attachments: T[];
  onDelete?: (attachment: T) => Promise<void>;
}

export const AttachmentsStateProvider = <T extends Attachment, >({ children, attachments, onDelete }: ProviderProps<T>) => {
  const [activeAttachmentNode, setActiveAttachmentNode] = useState<DoublyLinkedListNode<T> | null>(null);

  const attachmentsList = useMemo(() => {
    const list = new DoublyLinkedList<T>();
    attachments.forEach(attachment => list.add(attachment));
    return list;
  }, [attachments]);

  useEffect(() => {
    setActiveAttachmentNode(currentActiveAttachment => {
      if (!currentActiveAttachment
        || !attachmentsList.head
        || !attachmentsList.tail
      ) return null;

      const findAttachmentNode = (targetAttachment: T) => {
        for (const attachment of attachmentsList) {
          if (attachment?.value.url === targetAttachment.url) {
            return attachment;
          }
        }
        return null;
      };

      const sameAttachment = findAttachmentNode(currentActiveAttachment.value);
      if (sameAttachment) return sameAttachment;

      const findAvailableAttachment = (searchDirection: 'next' | 'prev') => {
        let foundAttachment = null;
        let currentAttachment = currentActiveAttachment[searchDirection];
        while (!foundAttachment && currentAttachment) {
          foundAttachment = findAttachmentNode(currentAttachment.value);
          currentAttachment = currentAttachment[searchDirection];
        }
        return foundAttachment;
      };

      return findAvailableAttachment('prev') ||
        findAvailableAttachment('next');
    });
  }, [attachmentsList]);

  const setCurrentAttachment = (node: DoublyLinkedListNode<T> | null | undefined) => {
    if (node) setActiveAttachmentNode(node);
  };

  const clearActiveAttachment = () => setActiveAttachmentNode(null);

  const moveToNext = () => {
    setCurrentAttachment(activeAttachmentNode?.next);
  };

  const moveToPrevious = () => {
    setCurrentAttachment(activeAttachmentNode?.prev);
  };

  const onAttachmentRemove = async () => {
    if (onDelete && activeAttachmentNode) {
      onDelete(activeAttachmentNode.value);
    }
  };

  const isPrevDisabled = !activeAttachmentNode?.prev;
  const isNextDisabled = !activeAttachmentNode?.next;

  return (
    <AttachmentsStateContext.Provider value={{
      activeAttachmentNode,
      attachmentsList,
      isPrevDisabled,
      isNextDisabled,
      moveToNext,
      moveToPrevious,
      onAttachmentRemove,
      clearActiveAttachment,
      setCurrentAttachment,
    }}>
      {children}
    </AttachmentsStateContext.Provider>
  );
};